<template>
  <div id="Profile" class="h-screen w-screen flex flex-col overflow-hidden relative profile-bg"
    :class="{ 'xl:h-auto xl:w-auto xl:overflow-visible': isDesktop }"
    style="font-family: 'Lato', sans-serif;">

    <div class="flex flex-col flex-1 min-h-0 relative z-10" :class="{ 'xl:flex-none': isDesktop }">

      <!-- Header: back + title (mobile only) -->
      <div v-if="!isDesktop" class="pt-safe flex items-center gap-3 px-safe pb-3 profile-header" :class="{ scrolled: isScrolled }">
        <button @click="backTo({ name: 'Dashboard' })"
          class="w-10 h-10 rounded-xl flex items-center justify-center bg-card profile-back-btn active:scale-95 transition-transform duration-150 shrink-0">
          <ArrowLeftIcon class="h-[18px] w-[18px] text-foreground" />
        </button>
        <p class="text-[16px] font-bold text-foreground">
          {{ $t('profile.headline') }}
        </p>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 min-h-0 overflow-y-auto pb-safe px-safe-flush profile-scroll"
        :class="{ 'xl:flex-none xl:overflow-visible xl:pb-0 xl:px-0': isDesktop }"
        @scroll="onScroll">

        <!-- Subtitle -->
        <p class="text-[13px] leading-relaxed mt-4 mb-5 px-6 text-muted-foreground">
          {{ $t('profile.subtitle') }}
        </p>

        <!-- Section: Center settings -->
        <div v-if="!userManagerStore.isExternalSession" class="px-5 mb-4">
          <div class="flex items-center gap-2 mb-2.5 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #FF9E1C, #E07A00);"></div>
            <h2 class="text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              {{ $t('profile.centerSettings') }}
            </h2>
          </div>
          <div class="profile-card rounded-xl overflow-hidden">
            <button v-for="(link, i) in visibleCenterLinks" :key="'center-' + i"
              @click="push(link.to)" data-testid="center-item"
              class="profile-link-item w-full flex items-center gap-3.5 px-4 py-3.5 active:scale-[0.99] transition-all duration-100 profile-link-divider">
              <component :is="link.icon" class="w-5 h-5 shrink-0 text-foreground/50" />
              <span class="flex-1 text-left text-[14px] font-semibold text-foreground truncate">{{ link.label }}</span>
              <div class="flex items-center gap-1.5 shrink-0">
                <span v-for="(badge, bi) in link.badges" :key="bi"
                  class="profile-badge px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
                  {{ badge.label }}
                </span>
                <ChevronRightIcon class="w-4 h-4 text-muted-foreground/40" />
              </div>
            </button>
            <!-- Expand / collapse -->
            <button v-if="hiddenCentersCount > 0"
              @click="centersExpanded = !centersExpanded"
              class="profile-link-item profile-link-divider w-full flex items-center justify-center gap-2 px-4 py-3 active:scale-[0.99] transition-all duration-100">
              <span class="text-[13px] font-semibold text-muted-foreground">
                {{ centersExpanded ? $t('profile.centers.show_less') : $t('profile.centers.show_more', { count: hiddenCentersCount }) }}
              </span>
              <ChevronDownIcon class="w-4 h-4 text-muted-foreground/40 transition-transform duration-200"
                :class="{ 'rotate-180': centersExpanded }" />
            </button>
            <!-- Add center -->
            <button @click="push({ name: 'CenterSignin', query: { context: 'add' } })"
              class="profile-link-item w-full flex items-center gap-3.5 px-4 py-3.5 active:scale-[0.99] transition-all duration-100">
              <PlusCircleIcon class="w-5 h-5 shrink-0" style="color: #FF9E1C;" />
              <span class="flex-1 text-left text-[14px] font-semibold" style="color: #FF9E1C;">{{ $t('profile.links.add_center') }}</span>
            </button>
          </div>
        </div>

        <!-- Section: Account settings -->
        <div class="px-5 mb-4">
          <div class="flex items-center gap-2 mb-2.5 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              {{ $t('profile.accountSettings') }}
            </h2>
          </div>
          <div class="profile-card rounded-xl overflow-hidden">
            <button v-for="(link, i) in accountLinks" :key="'account-' + i"
              @click="push(link.to)"
              class="profile-link-item w-full flex items-center gap-3.5 px-4 py-3.5 active:scale-[0.99] transition-all duration-100"
              :class="{ 'profile-link-divider': i < accountLinks.length - 1 }">
              <component :is="link.icon" class="w-5 h-5 shrink-0 text-foreground/50" />
              <span class="flex-1 text-left text-[14px] font-semibold text-foreground">{{ link.label }}</span>
              <ChevronRightIcon class="w-4 h-4 text-muted-foreground/40" />
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeMount, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import { AdjustmentsVerticalIcon, PlusCircleIcon, ShieldCheckIcon, DevicePhoneMobileIcon, InformationCircleIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import { LinkListLink } from '../GoManager';
import { useAppRouter } from '../composables/useAppRouter';
import { useIsDesktop } from '../composables/useIsDesktop';
import { useUserManagerStore } from '../stores/userManagerStore';
import log from '../utils/logger';

const { t } = useI18n();
const router = useRouter();
const { push, backTo } = useAppRouter();
const isDesktop = useIsDesktop();
const userManagerStore = useUserManagerStore();

// Desktop: redirect /profile to first center's settings (sidebar handles navigation)
watch(isDesktop, (desktop) => {
  if (desktop) {
    const firstCenter = userManagerStore.user?.centers?.[0];
    if (firstCenter) {
      router.replace({ name: 'ProfileCenter', params: { id: firstCenter.id } });
    } else {
      router.replace({ name: 'DisplaySettings' });
    }
  }
}, { immediate: true });

log.debug(`[GO-DEBUG][PROFILE] ▶ setup START`)
log.debug(`[GO-DEBUG][PROFILE] user=${!!userManagerStore.user}`)
log.debug(`[GO-DEBUG][PROFILE] user?.centers=${JSON.stringify(userManagerStore.user?.centers?.map(c => ({ id: c.id, title: c.title })) ?? 'null')}`)
log.debug(`[GO-DEBUG][PROFILE] displayedCenters=${userManagerStore.displayedCenters?.length ?? 'N/A'}`)

onBeforeMount(() => {
  log.debug(`[GO-DEBUG][PROFILE] onBeforeMount`)
})

onMounted(() => {
  log.debug(`[GO-DEBUG][PROFILE] onMounted — component is in the DOM`)
  const el = document.getElementById('Profile')
  if (el) {
    const rect = el.getBoundingClientRect()
    const styles = window.getComputedStyle(el)
    log.debug(`[GO-DEBUG][PROFILE] DOM element found: ${rect.width}x${rect.height}, opacity=${styles.opacity}, visibility=${styles.visibility}, display=${styles.display}`)
    log.debug(`[GO-DEBUG][PROFILE] classes on #Profile:`, el.className)
    log.debug(`[GO-DEBUG][PROFILE] parent element:`, el.parentElement?.tagName, el.parentElement?.className)
  } else {
    log.debug(`[GO-DEBUG][PROFILE] ✗ DOM element #Profile NOT found!`)
  }
})

onBeforeUnmount(() => {
  log.debug(`[GO-DEBUG][PROFILE] onBeforeUnmount`)
})

const isScrolled = ref(false);
function onScroll(e: Event) {
  isScrolled.value = (e.target as HTMLElement).scrollTop > 8;
}

const centerLinks = computed<Array<LinkListLink>>(() => {
  const centers = userManagerStore.user?.centers ?? [];
  log.debug(`[GO-DEBUG][PROFILE] centerLinks computed: ${centers.length} centers`)
  return centers.map(center => {
    const link: LinkListLink = {
      label: center.preferences?.label || center.title,
      to: { name: 'ProfileCenter', params: { id: center.id } },
      icon: AdjustmentsVerticalIcon,
      badges: [],
    };
    if (center.preferences && !center.preferences.display) {
      link.badges!.push({ label: t('profile.disabled'), color: 'red' });
    } else if (center.preferences && !center.preferences.in_dashboard) {
      link.badges!.push({ label: t('profile.hidden'), color: 'red' });
    }
    return link;
  });
});

const CENTERS_COLLAPSED_COUNT = 3;
const centersExpanded = ref(false);
const visibleCenterLinks = computed(() => {
  if (centersExpanded.value || centerLinks.value.length <= CENTERS_COLLAPSED_COUNT) {
    return centerLinks.value;
  }
  return centerLinks.value.slice(0, CENTERS_COLLAPSED_COUNT);
});
const hiddenCentersCount = computed(() => {
  return Math.max(0, centerLinks.value.length - CENTERS_COLLAPSED_COUNT);
});

const accountLinks = computed<Array<LinkListLink>>(() => {
  const links: Array<LinkListLink> = [
    { label: t('profile.links.display'), to: { name: 'DisplaySettings' }, icon: DevicePhoneMobileIcon },
  ]
  if (!userManagerStore.isExternalSession) {
    links.push({ label: t('profile.links.security'), to: { name: 'Security' }, icon: ShieldCheckIcon })
  }
  links.push({ label: t('profile.links.about'), to: { name: 'About' }, icon: InformationCircleIcon })
  return links
});
</script>

<style scoped>
/* Light mode */
.profile-header {
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease, backdrop-filter 0.2s ease;
}
.profile-header.scrolled {
  border-bottom-color: rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.profile-bg {
  background: transparent;
}
.profile-back-btn {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.profile-card {
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.profile-link-item:active {
  background: rgba(0, 0, 0, 0.02);
}
.profile-link-divider {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.profile-badge {
  background-color: rgba(227, 33, 28, 0.08);
  color: #E3211C;
}
.profile-scroll {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.profile-scroll::-webkit-scrollbar {
  display: none;
}

/* Dark mode */
:is(.dark *).profile-header.scrolled {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
:is(.dark *).profile-bg {
  background: transparent;
}
:is(.dark *).profile-back-btn {
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.05);
}
:is(.dark *).profile-card {
  background: #1E2024;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
:is(.dark *).profile-link-item:active {
  background: rgba(255, 255, 255, 0.03);
}
:is(.dark *).profile-link-divider {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).profile-badge {
  background-color: rgba(227, 33, 28, 0.15);
}
</style>
